import { useSelector, useDispatch } from 'react-redux';
import { selectOMDetailsSelectedOrder } from 'store/slices/order_management/details/selectors';
import { useSearchParams } from 'react-router-dom';
import { Order } from 'types';
import { setOMSelectedOrder } from 'store/slices/order_management/details';
import { notificationSlice } from 'store/slices/notificationSlice';
import React from 'react';

export const useInitiateOrderManagementConfig = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const orderNumberQueryParam = searchParams.get('order_number');
  const viewTabQueryParam = searchParams.get('view');
  const filterQueryParam = searchParams.get('filter');

  const setSelectedOrderFromOrderNumberQuery =
    useSetSelectedOrderFromOrderNumberQuery(
      orderNumberQueryParam,
      searchParams,
      setSearchParams
    );

  const updateOrderNumberQuery = useUpdateOrderNumberQuery(
    searchParams,
    setSearchParams
  );

  return {
    orderNumberQueryParam,
    setSelectedOrderFromOrderNumberQuery,
    updateOrderNumberQuery,
  };
};

const useSetSelectedOrderFromOrderNumberQuery = (
  orderNumber: string | null,
  searchParams: URLSearchParams,
  setSearchParams: (cb: (params: URLSearchParams) => URLSearchParams) => void
) => {
  const dispatch = useDispatch();
  const selectedOrder = useSelector(selectOMDetailsSelectedOrder);

  return React.useCallback(
    (
      orders: Order[],
      onSuccessCb?: (order: Order) => void
    ): Order | undefined => {
      if (!orderNumber || selectedOrder?.orderNumber === orderNumber) return;
      const matchingOrder = orders.find(
        (order) => order.orderNumber === orderNumber
      );

      if (!matchingOrder) {
        dispatch(
          notificationSlice.actions.setNotice({
            showNotice: true,
            noticeContent: `Could not find order with order number ${orderNumber}`,
          })
        );
        setSearchParams((prev: URLSearchParams) => {
          prev.delete('order_number');
          return prev;
        });
      } else {
        dispatch(setOMSelectedOrder(matchingOrder));
        onSuccessCb?.(matchingOrder);
      }
    },
    [orderNumber, selectedOrder, searchParams]
  );
};

const useUpdateOrderNumberQuery = (
  searchParams: URLSearchParams,
  setSearchParams: (cb: (params: URLSearchParams) => URLSearchParams) => void
) => {
  const orderNumberParam = searchParams.get('order_number');
  return React.useCallback(
    (orderNumber?: string) => {
      if ((orderNumber ?? null) === orderNumberParam) return;
      setSearchParams((prev: URLSearchParams) => {
        if (orderNumber) {
          prev.set('order_number', orderNumber);
        } else {
          prev.delete('order_number');
        }
        return prev;
      });
    },
    [orderNumberParam, setSearchParams]
  );
};

export default useInitiateOrderManagementConfig;
